import { z } from 'zod'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import { captureAndReturn } from '@omnea-app/packages/universal/zod-utils/captureAndReturn'
import { comparisonSchemasFor, ComparisonTree } from './schema'

/**
 * A condition evaluated against a person rather than a request — the manager-walk predicate, which
 * asks whether a candidate above the start user qualifies.
 *
 * Only the left operands are its own; operators, right operands and the AND/OR tree mean the same
 * thing against either subject and are shared. `ComparisonTree` carries why this cannot simply join
 * `Comparison`.
 */

const UserAttributeOperandSchema = z.object({
  type: z.literal('USER_ATTRIBUTE'),
  customFieldId: ref('customField'),
  // Not used to resolve the dataset: evaluation takes it from the attribute's definition. Still written for compatibility.
  source: ref('coreDataSet').optional(),
})
type UserAttributeOperand = z.infer<typeof UserAttributeOperandSchema>

/**
 * Something the walk knows about a candidate rather than something the person carries: the same
 * person is one step above one requester and three above another. A new such property is a value
 * here, not another operand.
 */
const CandidatePropertyOperandSchema = z.object({
  type: z.literal('CANDIDATE_PROPERTY'),
  value: z.enum(['stepsAboveStartUser']),
})
type CandidatePropertyOperand = z.infer<typeof CandidatePropertyOperandSchema>

const UserLeftOperandSchema = z
  .discriminatedUnion('type', [UserAttributeOperandSchema, CandidatePropertyOperandSchema])
  .catch(captureAndReturn('UserLeftOperandSchema'))
export type UserLeftOperand = z.infer<typeof UserLeftOperandSchema>

// Unannotated on purpose: the request tree is published as `Shared:Comparison`, and a left operand
// only this subject understands cannot appear in a shared client type.
const userComparison = comparisonSchemasFor(UserLeftOperandSchema)

const UserSingleComparisonSchema = userComparison.single
export type UserSingleComparison = z.infer<typeof UserSingleComparisonSchema>

const UserComparisonSchema = userComparison.tree
export type UserComparison = ComparisonTree<UserSingleComparison>

export const UserConditionConfigSchema = z.object({ name: z.string(), config: UserComparisonSchema })
export type UserConditionConfig = z.infer<typeof UserConditionConfigSchema>

export const createUserAttributeOperand = (customFieldId: string, source?: string): UserAttributeOperand => ({
  type: 'USER_ATTRIBUTE',
  customFieldId,
  ...(source === undefined ? {} : { source }),
})

export const createStepsAboveStartUserOperand = (): CandidatePropertyOperand => ({
  type: 'CANDIDATE_PROPERTY',
  value: 'stepsAboveStartUser',
})

export const isStepsAboveStartUserOperand = (operand: UserLeftOperand | null): boolean =>
  operand?.type === 'CANDIDATE_PROPERTY' && operand.value === 'stepsAboveStartUser'
